import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, MapPin, ArrowUpRight, Building2, Wrench, Target } from 'lucide-react';
import { getItem, getContent, asset } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { SectionHeading } from '@/components/SectionHeading';
import { Reveal } from '@/components/Reveal';
import { MagneticButton } from '@/components/MagneticButton';
import { Loader } from '@/components/Loader';
import { CtaSection } from '@/components/CtaSection';

const Field = ({ label, value }) => (
  <div className='border-t border-[#20272A]/10 pt-4'>
    <div className='text-[11px] uppercase tracking-wider text-[#20272A]/40'>{label}</div>
    <div className='mt-1 text-[#20272A]'>{value || <span className='text-[#20272A]/35'>To be added</span>}</div>
  </div>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProject(null); setNotFound(false);
    getItem('projects', slug).then(setProject).catch(() => setNotFound(true));
    getContent('projects').then(setAll).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className='flex min-h-[70vh] flex-col items-center justify-center bg-[#20272A] text-center'>
        <p className='eyebrow text-[#B88A3B]'>Project not found</p>
        <Link to='/projects' className='mt-4 text-[#F6F5F1] underline'>Back to projects</Link>
      </div>
    );
  }
  if (!project) return <Loader label='Loading project' />;

  const related = all.filter((p) => p.slug !== project.slug && !p.placeholder).slice(0, 3);
  const images = project.projectImages?.length ? project.projectImages : [project.heroImage];

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'CreativeWork', name: project.projectTitle,
    about: (project.serviceCategory || []).join(', '), locationCreated: project.location,
    creator: { '@type': 'Organization', name: 'Sri Lakshmi Enterprises' },
  };

  return (
    <>
      <Seo title={project.projectTitle} description={`${(project.serviceCategory || []).join(', ')} for ${project.client} at ${project.location}.`} path={`/projects/${project.slug}`} image={project.heroImage} jsonLd={jsonLd} />
      <PageHero
        eyebrow={project.client}
        title={project.associatedBrand || project.projectTitle}
        subtitle={`${(project.serviceCategory || []).join(' · ')}`}
        image={project.heroImage}
        meta={(
          <div className='flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#F6F5F1]/75'>
            <span className='inline-flex items-center gap-2'><MapPin size={15} className='text-[#B88A3B]' /> {project.location}</span>
            <span className='inline-flex items-center gap-2'><Building2 size={15} className='text-[#B88A3B]' /> {project.client}</span>
          </div>
        )}
      />

      {/* Overview */}
      <section className='bg-[#F6F5F1] py-20'>
        <div className='container-x grid gap-14 lg:grid-cols-12'>
          <div className='lg:col-span-7'>
            <SectionHeading eyebrow='Project Overview' title='Work executed on the ground.' />
            <Reveal delay={0.08}>
              <p className='mt-6 leading-relaxed text-[#20272A]/70'>
                {project.workDescription} carried out for {project.client}
                {project.associatedBrand ? ` (${project.associatedBrand})` : ''} at {project.location}.
              </p>
            </Reveal>
            <div className='mt-10 grid gap-x-10 gap-y-6 sm:grid-cols-2'>
              <Field label='Client' value={project.client} />
              <Field label='Vendor' value={project.vendor} />
              <Field label='Project Type' value={(project.serviceCategory || []).join(', ')} />
              <Field label='Location' value={project.location} />
              <Field label='Work Executed' value={project.workDescription} />
              <Field label='Project Year' value={project.projectYear} />
            </div>
          </div>
          <div className='lg:col-span-5'>
            <div className='space-y-6'>
              <div className='rounded-2xl border border-[#20272A]/10 bg-white p-7'>
                <div className='flex items-center gap-2 text-[#075B61]'><Target size={18} /><span className='eyebrow text-[#075B61]'>Challenge</span></div>
                <p className='mt-3 text-sm text-[#20272A]/70'>{project.challenge}</p>
              </div>
              <div className='rounded-2xl border border-[#20272A]/10 bg-white p-7'>
                <div className='flex items-center gap-2 text-[#075B61]'><Wrench size={18} /><span className='eyebrow text-[#075B61]'>Sri Lakshmi's Solution</span></div>
                <p className='mt-3 text-sm text-[#20272A]/70'>{project.solution}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Execution gallery */}
      <section className='relative bg-[#20272A] py-16'>
        <div className='grain absolute inset-0' />
        <div className='relative container-x'>
          <SectionHeading eyebrow='Execution' title='On-site imagery.' light />
          <div className='mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {images.map((img, i) => (
              <div key={i} className='overflow-hidden rounded-xl border border-white/10'>
                <img src={asset(img)} alt={`${project.projectTitle} ${i + 1}`} className='h-60 w-full object-cover transition-transform duration-700 hover:scale-105' />
              </div>
            ))}
          </div>
          <p className='mt-4 text-xs text-[#F6F5F1]/40'>Additional project imagery to be uploaded.</p>
        </div>
      </section>

      {/* Equipment / Outcome / Feedback */}
      <section className='bg-[#F6F5F1] py-20'>
        <div className='container-x grid gap-8 lg:grid-cols-3'>
          <div className='rounded-2xl border border-[#20272A]/10 bg-white p-7'>
            <span className='eyebrow text-[#075B61]'>Equipment Used</span>
            {project.equipmentUsed?.length ? (
              <div className='mt-4 flex flex-wrap gap-2'>
                {project.equipmentUsed.map((e) => <span key={e} className='rounded-full border border-[#20272A]/12 px-3 py-1 text-xs text-[#20272A]/70'>{e}</span>)}
              </div>
            ) : (
              <p className='mt-3 text-sm text-[#20272A]/45'>Content to be updated.</p>
            )}
          </div>
          <div className='rounded-2xl border border-[#20272A]/10 bg-white p-7'>
            <span className='eyebrow text-[#075B61]'>Outcome</span>
            <p className='mt-3 text-sm text-[#20272A]/45'>{project.projectOutcome || 'Content to be updated.'}</p>
          </div>
          <div className='rounded-2xl border border-[#20272A]/10 bg-white p-7'>
            <span className='eyebrow text-[#075B61]'>Client Feedback</span>
            <p className='mt-3 text-sm italic text-[#20272A]/45'>“{project.testimonial}”</p>
            <div className='mt-4 flex items-center gap-1'>
              {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={16} className='text-[#20272A]/20' />)}
            </div>
            <p className='mt-2 text-[11px] uppercase tracking-wider text-[#20272A]/35'>{project.ratingLabel}</p>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className='bg-[#F6F5F1] pb-20'>
          <div className='container-x'>
            <SectionHeading eyebrow='Related Projects' title='More from our portfolio.' />
            <div className='mt-10 grid gap-6 md:grid-cols-3'>
              {related.map((p) => (
                <Link key={p.slug} to={`/projects/${p.slug}`} data-cursor='VIEW PROJECT' className='group block overflow-hidden rounded-2xl border border-[#20272A]/10 bg-white'>
                  <div className='relative h-52 overflow-hidden'>
                    <img src={asset(p.heroImage)} alt={p.projectTitle} className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105' />
                  </div>
                  <div className='flex items-center justify-between p-5'>
                    <div>
                      <div className='flex items-center gap-1.5 text-xs text-[#20272A]/50'><MapPin size={13} className='text-[#B88A3B]' /> {p.location}</div>
                      <h3 className='mt-1.5 font-head text-lg font-semibold text-[#20272A]'>{p.associatedBrand || p.projectTitle}</h3>
                    </div>
                    <ArrowUpRight size={20} className='text-[#B88A3B]' />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaSection />
    </>
  );
}
